import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ButtonBase from "@mui/material/ButtonBase";
import { styled } from "@mui/material/styles";
import AssistantTurnStep from "ai-assistant/turn-steps/assistant-turn-step";
import { useId, useState } from "react";

const SummaryButton = styled(ButtonBase)(({ theme }) => ({
  alignItems: "center",
  borderRadius: theme.shape.borderRadius,
  color: theme.palette.text.secondary,
  display: "flex",
  gap: theme.spacing(0.5),
  ...theme.typography.caption,
  marginTop: theme.spacing(1),
  maxWidth: "100%",
  textAlign: "left",
  "&:focus-visible": {
    outline: `2px solid ${theme.palette.primary.main}`,
    outlineOffset: 2,
  },
}));

// One line while collapsed, however many steps there are: the answer is what the reader came for.
const SummaryText = styled("span")({
  minWidth: 0,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
});

const ExpandIcon = styled(ExpandMoreIcon, {
  shouldForwardProp: (prop) => prop !== "expanded",
})<{ expanded: boolean }>(({ theme, expanded }) => ({
  flexShrink: 0,
  fontSize: theme.typography.body2.fontSize,
  transform: expanded ? "rotate(180deg)" : "none",
  transition: theme.transitions.create("transform", { duration: theme.transitions.duration.shortest }),
}));

const StepList = styled("ol")(({ theme }) => ({
  color: theme.palette.text.secondary,
  ...theme.typography.caption,
  margin: theme.spacing(0.5, 0, 0),
  paddingLeft: theme.spacing(2.5),
}));

/**
 * What the assistant did on the way to an answer, as one collapsed line beneath it (#7284):
 * "Searched work records · Looked up a proposal · Checked 4 citations". Expanded, one step a line.
 *
 * Every label is the server's, from a fixed list - never the model's words, and never what a lookup
 * was asked or found - so the line can be shown as it arrives. It is not announced: the panel's live
 * region already said the assistant was working, and the answer is what it announces next.
 */
export default function TurnStepsSummary({ steps }: Readonly<{ steps: readonly AssistantTurnStep[] }>) {
  const [expanded, setExpanded] = useState(false);
  const listId = useId();
  if (steps.length === 0) return null;

  const phrases = steps.map((step) => step.describe());
  return (
    <div>
      <SummaryButton
        aria-expanded={expanded}
        aria-controls={expanded ? listId : undefined}
        onClick={() => setExpanded((open) => !open)}
      >
        <SummaryText>{expanded ? "Steps the assistant took" : phrases.join(" · ")}</SummaryText>
        <ExpandIcon expanded={expanded} aria-hidden />
      </SummaryButton>
      {expanded && (
        <StepList id={listId}>
          {phrases.map((phrase, index) => (
            // The same step can appear twice - a search, a lookup, the search again - so its
            // position is part of its key.
            <li key={`${index}-${phrase}`}>{phrase}</li>
          ))}
        </StepList>
      )}
    </div>
  );
}
